// Route planning for a technician's day.
//
// Distances are straight-line miles times a road factor — no map service or
// API key needed, and close enough to order stops well in a metro area. The
// order is built nearest-neighbour from the office, then improved with 2-opt
// (reversing any stretch of the route that shortens it, until nothing does).

export const HQ = { lat: 33.8314, lng: -118.2820 }; // Carson office
const ROAD_FACTOR = 1.3;
const AVG_MPH = 25;

export const haversine = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return 0;
  const R = 3958.8;
  const toR = (x) => (x * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat);
  const dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export const roadMiles = (a, b) => haversine(a, b) * ROAD_FACTOR;

/** Total road miles from the office through the stops in order. */
export function routeMiles(points, start = HQ) {
  let miles = 0;
  let prev = start;
  for (const p of points) {
    if (p?.lat == null) continue;
    miles += roadMiles(prev, p);
    prev = p;
  }
  return Math.round(miles * 10) / 10;
}

/** Best order for the stops that have coordinates; stops without stay at the end, as given. */
export function optimizeOrder(stops, coordOf, start = HQ) {
  const located = stops.filter((s) => coordOf(s)?.lat != null);
  const rest = stops.filter((s) => coordOf(s)?.lat == null);
  if (located.length < 3) {
    // Two stops: just take the nearer one first.
    const ordered = [...located].sort((a, b) => roadMiles(start, coordOf(a)) - roadMiles(start, coordOf(b)));
    return [...ordered, ...rest];
  }
  // Nearest neighbour.
  const remaining = [...located];
  let order = [];
  let cur = start;
  while (remaining.length) {
    let bi = 0;
    let bd = Infinity;
    remaining.forEach((s, i) => {
      const d = roadMiles(cur, coordOf(s));
      if (d < bd) { bd = d; bi = i; }
    });
    const next = remaining.splice(bi, 1)[0];
    order.push(next);
    cur = coordOf(next);
  }
  // 2-opt: reverse segments while it shortens the route.
  const pts = () => order.map(coordOf);
  let best = routeMiles(pts(), start);
  let improved = true;
  let guard = 0;
  while (improved && guard++ < 50) {
    improved = false;
    for (let i = 0; i < order.length - 1; i += 1) {
      for (let k = i + 1; k < order.length; k += 1) {
        const candidate = [...order.slice(0, i), ...order.slice(i, k + 1).reverse(), ...order.slice(k + 1)];
        const miles = routeMiles(candidate.map(coordOf), start);
        if (miles < best - 0.05) {
          order = candidate;
          best = miles;
          improved = true;
        }
      }
    }
  }
  return [...order, ...rest];
}

/** Estimated arrival for each stop: start time, drive at AVG_MPH, minutes on site per stop. */
export function arrivalTimes(stops, coordOf, { startMinutes = 8 * 60, serviceMinutes = 30, start = HQ } = {}) {
  let clock = startMinutes;
  let prev = start;
  return stops.map((s) => {
    const c = coordOf(s);
    if (c?.lat != null) {
      clock += Math.round((roadMiles(prev, c) / AVG_MPH) * 60);
      prev = c;
    }
    const eta = clock;
    clock += serviceMinutes;
    return eta;
  });
}

/** 8:35 → "8:45 AM" (rounded up to the quarter hour, the way appointments are given). */
export function clockLabel(minutes, round = 15) {
  const m = Math.ceil(minutes / round) * round;
  const h24 = Math.floor(m / 60) % 24;
  const mm = String(m % 60).padStart(2, '0');
  const ap = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${ap}`;
}
